import type {
  PortalAccountContact,
  PortalApplicationState,
  PortalDocumentItem,
  PortalLoanSummary,
  PortalPaymentHistoryItem,
  PendingPaymentDraft,
} from './types';

const initialLoan: PortalLoanSummary = {
  id: 'mock-loan-1',
  loanNumber: 'ELF-10482',
  programLabel: 'Educational Loan Fund (ELF)',
  status: 'Current',
  principalBalance: 12450.0,
  interestBalance: 186.42,
  totalBalance: 12636.42,
  nextPaymentAmount: 225.0,
  nextPaymentDate: '2026-09-01',
  currency: 'USD',
};

const initialHistory: PortalPaymentHistoryItem[] = [
  {
    id: 'pay-3',
    date: '2026-08-01',
    description: 'Scheduled installment',
    amount: 225.0,
    status: 'Posted',
  },
  {
    id: 'pay-2',
    date: '2026-07-01',
    description: 'Scheduled installment',
    amount: 225.0,
    status: 'Posted',
  },
  {
    id: 'pay-1',
    date: '2026-06-01',
    description: 'Scheduled installment',
    amount: 225.0,
    status: 'Posted',
  },
  {
    id: 'pay-0',
    date: '2026-05-15',
    description: 'Additional principal',
    amount: 100.0,
    status: 'Posted',
  },
];

const initialDocuments: PortalDocumentItem[] = [
  {
    id: 'doc-tax-2025',
    category: 'tax',
    title: '2025 Form 1098-E',
    description: 'Student loan interest statement for tax year 2025.',
    fileName: '1098-E-2025-sample.txt',
  },
  {
    id: 'doc-tax-2024',
    category: 'tax',
    title: '2024 Form 1098-E',
    description: 'Student loan interest statement for tax year 2024.',
    fileName: '1098-E-2024-sample.txt',
  },
  {
    id: 'doc-coupon',
    category: 'payment_coupons',
    title: 'Payment coupon booklet',
    description: 'Printable coupons for mail-in payments.',
    fileName: 'payment-coupons-sample.txt',
  },
  {
    id: 'doc-stmt-aug',
    category: 'statements',
    title: 'August 2026 statement',
    description: 'Monthly account statement.',
    fileName: 'statement-2026-08-sample.txt',
  },
  {
    id: 'doc-stmt-jul',
    category: 'statements',
    title: 'July 2026 statement',
    description: 'Monthly account statement.',
    fileName: 'statement-2026-07-sample.txt',
  },
  {
    id: 'doc-welcome',
    category: 'welcome',
    title: 'Welcome packet',
    description: 'Loan terms overview and borrower guide.',
    fileName: 'welcome-packet-sample.txt',
  },
];

const initialAccount: PortalAccountContact = {
  firstName: 'Jordan',
  lastName: 'Lee',
  email: 'jordan.lee@example.com',
  phone: '(515) 555-0142',
  addressLine1: '1842 Maple Avenue',
  addressLine2: 'Apt 4B',
  city: 'Des Moines',
  state: 'IA',
  postalCode: '50309',
};

const initialApplication: PortalApplicationState = {
  status: 'pending',
  lastUpdated: '2026-08-02',
  referenceId: 'APP-2026-8841',
};

/** Mutable in-memory mock store for the click-through prototype. */
let loan = { ...initialLoan };
let history = initialHistory.map((h) => ({ ...h }));
let documents = initialDocuments.map((d) => ({ ...d }));
let account = { ...initialAccount };
let application = { ...initialApplication };
let pendingPayment: PendingPaymentDraft | null = null;

const listeners = new Set<() => void>();

/** Bumped on each mutation so useSyncExternalStore getSnapshot can be referentially stable. */
let storeVersion = 0;

function notify(): void {
  storeVersion += 1;
  listeners.forEach((fn) => fn());
}

export function getMockPortalVersion(): number {
  return storeVersion;
}

export function subscribeMockPortal(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getMockLoan(): PortalLoanSummary {
  return { ...loan };
}

export function getMockPaymentHistory(): PortalPaymentHistoryItem[] {
  return history.map((h) => ({ ...h }));
}

export function getMockRecentActivity(limit = 3): PortalPaymentHistoryItem[] {
  return getMockPaymentHistory().slice(0, limit);
}

export function getMockDocuments(): PortalDocumentItem[] {
  return documents.map((d) => ({ ...d }));
}

export function getMockAccount(): PortalAccountContact {
  return { ...account };
}

export function saveMockAccount(next: PortalAccountContact): void {
  account = { ...next };
  notify();
}

export function getMockApplication(): PortalApplicationState {
  return { ...application };
}

export function startMockApplication(): void {
  application = {
    status: 'pending',
    lastUpdated: new Date().toISOString().slice(0, 10),
    referenceId: application.referenceId || 'APP-2026-8841',
  };
  notify();
}

export function setPendingPayment(draft: PendingPaymentDraft): void {
  pendingPayment = { ...draft };
}

export function getPendingPayment(): PendingPaymentDraft | null {
  return pendingPayment ? { ...pendingPayment } : null;
}

export function clearPendingPayment(): void {
  pendingPayment = null;
}

export function submitMockPayment(draft: PendingPaymentDraft): void {
  const id = `pay-${Date.now()}`;
  history = [
    {
      id,
      date: new Date().toISOString().slice(0, 10),
      description:
        draft.amountMode === 'installment'
          ? 'Scheduled installment'
          : 'Custom payment',
      amount: draft.amount,
      status: 'Posted',
    },
    ...history,
  ];
  // Reduce displayed total slightly for demo feel
  const applied = Math.min(draft.amount, loan.totalBalance);
  loan = {
    ...loan,
    totalBalance: Math.max(0, Number((loan.totalBalance - applied).toFixed(2))),
    principalBalance: Math.max(
      0,
      Number((loan.principalBalance - applied * 0.9).toFixed(2))
    ),
    interestBalance: Math.max(
      0,
      Number((loan.interestBalance - applied * 0.1).toFixed(2))
    ),
  };
  pendingPayment = null;
  notify();
}

export function formatPortalCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatPortalDate(isoDate: string | null): string {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const DOCUMENT_CATEGORY_LABELS: Record<
  PortalDocumentItem['category'],
  string
> = {
  tax: 'Tax documents',
  payment_coupons: 'Payment coupons',
  statements: 'Statements',
  welcome: 'Welcome materials',
};
